import axios from 'axios';
import i18n from 'i18next';
import { getBaseUrl } from 'home/DomainName/DomainConstants';
import theme from './styles/theme';

export const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
});

axiosInstance.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.params = {
      ...(config.params || {})
    };

    // No longer send language as a query parameter.
    // Backend / Babel use user `locale` (e.g. en, es, fr, de, ja); i18n loads `/locales/{locale}/`.
  }

  return config;
});

export const ENDPOINT = {
  LOGIN: 'login',
  SSO_LOGIN: 'saml/login',
  SIGNUP: 'signup',
  ASSOCIATE_TOKEN_DETAILS: 'associate-with-site',
  SIGNUP_TOKEN_DETAILS: 'signup-token-details',
  GENERATE_SIGNUP_TOKEN: 'generate-signup-token',
  GET_ALL_TIMEZONES: 'i/v1/timezones',
  CURRENT_USER: 'i/v1/current-user',
  DELETE_USER: user_id => `i/v1/users/${user_id}`,
  UPDATE_USER: user_id => `i/v1/users/${user_id}`,
  CREATE_USER: 'i/v1/users',
  GET_USERS: search_key => `i/v1/users?search_key=${encodeURIComponent(search_key)}`,
  SWITCH_ACCOUNT: 'i/v1/switch-account',
  LOGOUT: 'logout',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  VERIFY_USER_UPDATE_OTP: 'user-update-authorization',
  VERIFY_LOGIN_OTP: 'verify-login-otp',
  RESEND_LOGIN_OTP: 'send-login-otp',
  VERIFY_SIGNUP_OTP: 'verify-signup-otp',
  RESEND_SIGNUP_OTP: 'send-signup-otp',
  CHANGE_PASSWORD: 'change-password',
  UPDATE_ACCOUNT: 'i/v1/current-account',
  ACCOUNT_LOGO: 'i/v1/account-logo',
  SASS_AGREEMENT_ACTION: 'i/v1/accounts/online-agreement-action',
  GET_CURRENT_ACCOUNT: 'i/v1/current-account',
  GET_CURRENT_ACCOUNT_USERS: account_id => `i/v1/accounts/${account_id}/users`,
  UPDATE_PREFERENCES: 'i/v1/account-preferences',
  GET_ACCOUNT_PREFERENCES_METADATA: 'i/v1/account-preferences/metadata',
  UPDATE_EVENT_PREFERENCES: account_id => `i/v1/events/account-prefs/${account_id}`,
  GET_ORGANISATIONS: 'i/v1/organisations',
  CREATE_ORGANISATION: 'i/v1/organisations',
  GET_ORGANISATION: orgId => `i/v1/organisations/${orgId}`,
  DELETE_ORGANISATION: orgId => `i/v1/organisations/${orgId}`,
  UPDATE_ORGANISATION: orgId => `i/v1/organisations/${orgId}`,
  GET_ORGANISATION_ACCOUNTS: orgId => `i/v1/organisations/${orgId}/accounts`,
  ASSIGN_ACCOUNT_ORGANISATION: accountId => `i/v1/accounts/${accountId}/organisation`,
  TRANSFER_DEVICE_BETWEEN_ACCOUNTS: orgId => `i/v1/organisations/${orgId}/transfer-device`,
  GET_TASK_STATUS: task_id => `i/v1/async-task-status/${task_id}`,
  GET_DASHBOARD_CHART_DATA: 'i/v1/analytics/plant',
  GET_DASHBOARD_ACTIVITY: 'events/service/account-events',
  GET_BEARING_LIST: 'i/v1/bearing-models',
  ADD_BEARING: 'i/v1/bearing-models',
  UPDATE_BEARING: bearingId => `i/v1/bearing-models/${bearingId}`,
  GET_ALL_MANUFACTURERS: 'i/v1/bearing-models/manufacturers',
  DELETE_BEARING: bearingId => `i/v1/bearing-models/${bearingId}`,
  ASSOCIATE_BEARING: componentId => `i/v1/components/${componentId}/set-bearing`,
  UNASSIGN_BEARING: componentId => `i/v1/components/${componentId}/unassign-bearing`,
  GET_MACHINES_BUILDER_METADATA: 'i/v2/machines/metadata',
  GET_MACHINE_LIST: 'i/v1/machines',
  GET_MACHINES_CSV_COLUMNS: 'i/v1/machines/csv/columns',
  EXPORT_MACHINES_CSV: 'i/v1/machines/csv/export',
  PREVIEW_MACHINES_CSV_IMPORT: 'i/v1/machines/csv/import/preview',
  APPLY_MACHINES_CSV_IMPORT: 'i/v1/machines/csv/import/apply',
  GET_ATTENTION_SUMMARY: 'i/v1/machines/attention_summary',
  GET_MACHINE_DETAILS: machineId => `i/v1/machines/${machineId}`,
  GET_MACHINE_SUMMARY: machineId => `i/v1/analytics/machines/${machineId}`,
  GET_MACHINE_ANALYTICS: 'i/v1/analytics/machines/metadata',
  GET_MACHINE_TAG_LIMITS: 'i/v1/analytics/machines/alarm-limits',
  GET_HEALTH_REPORT: 'i/v1/analytics/machines/health-report',
  GET_MACHINE_COMPLETION_STATUS: machineId => `i/v1/analytics/machines/${machineId}/completion-status`,
  GET_MACHINE_SENSORS: machineId => `i/v1/machines/${machineId}/sensors`,
  GET_MACHINE_COMPONENTS: machineId => `i/v1/machines/${machineId}/components`,
  GET_MACHINE_COMPONENTS_LOCATIONS: machineId => `i/v1/machines/${machineId}/locations`,
  GET_MACHINE_DATA_OVERVIEW: machineId => `i/v1/machines/${machineId}/data-overview`,
  CREATE_MACHINE_COMPONENTS_LOCATIONS: machineId => `i/v1/machines/${machineId}/locations`,
  DELETE_MACHINE_COMPONENTS_LOCATIONS: (machineId, locationId) => `i/v1/machines/${machineId}/locations/${locationId}`,
  UPDATE_MACHINE_COMPONENTS_LOCATIONS: (machineId, locationId) => `i/v1/machines/${machineId}/locations/${locationId}`,
  GET_MOTE: moteId => `i/v1/motes/${moteId}`,
  GET_TX: txId => `i/v1/tx/${txId}`,
  DISSOCIATE_TX: txId => `i/v1/tx/${txId}/dissociate`,
  UPDATE_MACHINE_INFO: machineId => `i/v1/machines/${machineId}`,
  UPDATE_COMPONENT_INFO: componentId => `i/v1/components/${componentId}`,
  DELETE_COMPONENT: componentId => `i/v1/components/${componentId}`,
  UPDATE_COMPONENTS: machineId => `i/v2/machines/${machineId}/components`,
  REORDER_COMPONENTS: machineId => `i/v2/machines/${machineId}/reorder-components`,
  CREATE_COMPONENT: machineId => `i/v2/machines/${machineId}/components`,
  GET_COMPONENT_SVG: componentType => `i/v1/component-types/${componentType}/graphics`,
  GET_MACHINE_TAGS: machineId => `i/v1/machines/${machineId}/tags`,
  LOCATION: locationId => `i/v1/locations/${locationId}`,
  UPDATE_TAG: tagId => `i/v1/tags/${tagId}`,
  UPDATE_TAGS: machineId => `i/v1/machines/${machineId}/tags`,
  GET_TAG_TYPES: 'i/v1/tag-types',
  TRIGGER_PARAMETER_TYPES: 'i/v1/trigger-parameter-types',
  GET_TPI_TAG_TYPES: 'i/v1/all-tag-types',
  UPDATE_THIRD_PARTY_TAG: tagId => `i/v1/third-party-tags/${tagId}`,
  GET_TAG_TREND: tagId => `i/v1/tags/${tagId}/trend`,
  GET_TAG_WAVEFORM: tagId => `i/v1/tags/${tagId}/waveform`,
  GET_TAG_SPECTRUM: tagId => `i/v1/tags/${tagId}/spectrum`,
  GET_TAG_SPECTRUMS: tagId => `i/v1/tags/${tagId}/spectrums`,
  GET_TAG_CEPSTRUM: tagId => `i/v1/tags/${tagId}/cepstrum`,
  CREATE_ENVELOPE_THRESHOLDS: 'i/v1/tags/envelope-thresholds',
  GET_COMPUTED_ENVELOPE_THRESHOLDS: taskId => `i/v1/tags/envelope-thresholds/${taskId}`,
  CREATE_THIRD_PARTY_INTEGRATIONS: 'i/v1/third-party-integrations',
  UPDATE_THIRD_PARTY_INTEGRATIONS: integrationId => `i/v1/third-party-integrations/${integrationId}`,
  DELETE_THIRD_PARTY_INTEGRATIONS: integrationId => `i/v1/third-party-integrations/${integrationId}`,
  GET_THIRD_PARTY_INTEGRATION_DETAILS: integrationId => `i/v1/third-party-integrations/${integrationId}`,
  GET_THIRD_PARTY_INTEGRATION_LIST: 'i/v1/third-party-integrations',
  UPDATE_ATTENTION_MACHINE_READ: machineId => `i/v1/machines/${machineId}/read`,
  BULK_MARK_ATTENTION_READ: 'i/v1/machines/read',
  SNOOZE_MACHINE: machineId => `i/v1/machines/${machineId}/snooze`,
  SNOOZE_MACHINES: 'i/v1/machines/snooze',
  WATCHLIST: 'i/v1/users/me/watchlist',
  WATCHLIST_ITEM: machineId => `i/v1/users/me/watchlist/${machineId}`,
  ADD_NOTE: 'i/v1/machine-notes',
  NOTE: noteId => `i/v1/machine-notes/${noteId}`,
  ADD_TASK: 'i/v1/tasks',
  TASK: taskId => `i/v1/tasks/${taskId}`,
  ADD_REPAIR: 'i/v1/repairs',
  REPAIR: repairID => `i/v1/repairs/${repairID}`,
  CREATE_TEMPLATE: 'i/v1/task-templates',
  TEMPLATE_LIST: 'i/v1/task-templates',
  DELETE_TASK_TEMPLATE: templateID => `i/v1/task-templates/${templateID}`,
  UPDATE_TASK_TEMPLATE: templateID => `i/v1/task-templates/${templateID}`,
  DASHBOARDS: 'i/v1/dashboards',
  ACCOUNT_DASHBOARDS: 'i/v1/account-dashboards',
  // Site Overlay Image endpoints
  ACCOUNT_DASHBOARD_SITE_OVERLAY_IMAGE: 'i/v1/account-dashboard/site-overlay/image',
  ACCOUNT_DASHBOARD_SITE_OVERLAY_IMAGE_FILE: filename => `i/v1/account-dashboard/site-overlay/image/file/${filename}`,
  // Site Overlay Locations endpoint
  ACCOUNT_DASHBOARD_SITE_OVERLAY_LOCATIONS: 'i/v1/account-dashboard/site-overlay/locations',
  DELETE_DASHBOARD: dashboardId => `i/v1/dashboards/${dashboardId}`,
  DASHBOARD_SETTINGS: 'i/v1/dashboard-settings',
  WIDGETS_METADATA: 'i/v1/widgets/metadata',
  WIDGET_DATA: 'i/v1/widget-data',
  MACHINES: machineId => machineId ? `i/v2/machines/${machineId}` : 'i/v2/machines',
  MACHINE_SUB_TYPES: 'i/v1/machine-sub-types',
  GET_VIBRATION_MOTES: machineId => `webapp/machines/${machineId}/motes-info`,
  GET_VIBRATION_TXES: machineId => `webapp/machines/${machineId}/tx`,
  GET_MOTE_LIST: 'i/v1/motes',
  GET_TX_LIST: 'i/v1/tx',
  CREATE_MOTE: 'i/v1/motes',
  CREATE_TX: 'i/v1/tx',
  GET_FORCING_FREQUENCIES: tagId => `i/v1/tags/${tagId}/forcing-frequencies`,
  GET_MACHINE_PICTURES: machineId => `i/v1/machines/${machineId}/pictures`,
  DELETE_MACHINE_PICTURE: (machineId, pictureId) => `i/v1/machines/${machineId}/pictures/${pictureId}`,
  UPLOAD_MACHINE_PICTURE: machineId => `i/v1/machines/${machineId}/pictures`,
  UPDATE_MACHINE_PICTURE_LABEL: (machineId, pictureId) => `i/v1/machines/${machineId}/pictures/${pictureId}`,
  UPDATE_TAG_SPECTRUM_FEATURES: featureId => `i/v1/tag-spectrum-features/${featureId}`,
  GET_TAG_SPECTRUM_FEATURES: tagId => `i/v1/tag-spectrum-features/${tagId}`,
  CREATE_TAG_SPECTRUM_FEATURES: 'i/v1/tag-spectrum-features',
  GET_TAG_SPECTRUM_FEATURE_TREND: featureId => `i/v1/tag-spectrum-features/${featureId}/trend`,
  GET_TAG_SPECTRUM_FEATURE_TREND_TASK: taskId => `i/v1/tag-spectrum-features/trend-task/${taskId}`,
  GET_CUSTOM_FEATURE_TREND: tagId => `i/v1/tags/${tagId}/forcing-frequency-trend`,
  UPLOAD_ATTACHMENT: (origin_type, origin_id) => `i/v1/attachments/${origin_type}/${origin_id}`,
  DOWNLOAD_ATTACHMENT: (attachment_id, thumbnail) => `i/v1/attachments/${attachment_id}?thumbnail=${thumbnail}`,
  DELETE_ATTACHMENT: attachment_id => `i/v1/attachments/${attachment_id}`,
  GET_MACHINE_ATTACHMENTS: attachment_id => `i/v1/machines/${attachment_id}/attachments`,
  SENSOR_MODELS: model_id => model_id ? `i/v2/sensor-models/${model_id}` : 'i/v2/sensor-models',
  GET_SENSOR_MODEL_METADATA: 'i/v2/sensor-models/metadata',
  GET_SENSOR_INSTANCES: model_id => `i/v2/sensor-models/${model_id}/instances`,
  GET_TX_MODELS: 'i/v1/tx-models',
  GET_MOTE_MODELS: 'i/v1/mote-models',
  GET_DEFINITIONS: 'i/v1/event-templates',
  CREATE_DEFINITION: 'i/v1/event-templates',
  GET_EVENT_DEFINITION: template_id => `i/v1/event-templates/${template_id}`,
  DELETE_EVENT_DEFINITION: template_id => `i/v1/event-templates/${template_id}`,
  UPDATE_DEFINITION_TRIGGER: template_id => `i/v1/event-templates/${template_id}/trigger`,
  UPDATE_ACTION: template_id => `i/v1/event-templates/${template_id}`,
  UPDATE_SUBSCRIPTION: template_id => `i/v1/event-templates/${template_id}/subscriptions`,
  ADD_DEFINTION_RESOURCE: template_id => `i/v1/event-templates/${template_id}/resources`,
  SYNC_DEFINTION_RESOURCE: template_id => `i/v1/event-templates/${template_id}/sync-resources`,
  DELETE_DEFINTION_RESOURCE: template_id => `i/v1/event-templates/${template_id}/resources`,
  GET_FILTER_RESOURCES: 'i/v1/filter-resources',
  GET_EVENT_DEFINITION_METADATA: 'i/v1/event-templates-metadata',
  // Release Notes
  RELEASE_NOTES: 'i/v1/release-notes',
  RELEASE_NOTE: release_note_id => `i/v1/release-notes/${release_note_id}`,
  RELEASE_NOTES_BULK: 'i/v1/release-notes/bulk',
  RELEASE_NOTES_RELEASES: 'i/v1/release-notes/releases',
  RELEASE_NOTES_RELEASE: release_id => `i/v1/release-notes/releases/${release_id}`,
  RELEASE_NOTES_UNREAD_COUNT: 'i/v1/release-notes/unread-count',
  RELEASE_NOTES_SEEN: 'i/v1/release-notes/seen',
  RELEASE_NOTES_IMAGES: 'i/v1/release-notes/images',
  RELEASE_NOTES_IMAGE: image_id => `i/v1/release-notes/images/${image_id}`,
  RELEASE_NOTES_PETASENSE: 'i/v1/release-notes/petasense',
  RELEASE_NOTES_PETASENSE_DECISION: release_note_id => `i/v1/release-notes/petasense/${release_note_id}/decision`,
  RELEASE_NOTES_PETASENSE_TEMPLATE: release_note_id => `i/v1/release-notes/petasense/${release_note_id}/template`,
  GET_ACTIVITY: 'i/v1/activities',
  UPDATE_ACTIVITY: 'i/v1/activities',
  UPDATE_ACTIVITY_READ: 'i/v1/activities-read',
  MARK_EVENTS_AS_ANALYZED: 'i/v1/events/mark-analyzed',
  GET_EVENT_DETAILS: eventId => `i/v1/events/${eventId}`,
  POST_EVENT_COMMENT: eventId => `i/v1/events/${eventId}/comments`,
  EVENT_COMMENT: (eventId, commentId) => `i/v1/events/${eventId}/comments/${commentId}`,
  CLOSE_EVENT: eventId => `i/v1/events/${eventId}`,
  GET_NOTE_DETAILS: noteId => `i/v1/machine-notes/${noteId}`,
  GET_ACTIVITY_COMMENTS: (activity_type, activity_id) => `i/v1/activities/${activity_type}/${activity_id}/comments`,
  POST_COMMENT: (activity_type, activity_id) => `i/v1/activities/${activity_type}/${activity_id}/comments`,
  UPDATE_COMMENT: (activity_type, activity_id, commentId) => `i/v1/activities/${activity_type}/${activity_id}/comments/${commentId}`,
  DELETE_COMMENT: (activity_type, activity_id, commentId) => `i/v1/activities/${activity_type}/${activity_id}/comments/${commentId}`,
  NOTE_COMMENT: (noteId, commentId) => `i/v1/machine-notes/${noteId}/comments/${commentId}`,
  MARK_AS_ANALYSED: machineId => `/i/v1/machines/${machineId}/mark-analyzed`,
  // Instrumentation Alerts tab
  GET_INSTRUMENTATION_ALERTS: 'i/v1/instrumentation-alerts',
  UPDATE_INSTRUMENTATION_ALERTS: 'i/v1/instrumentation-alerts-status',
  // Overview tab
  GET_MACHINE_CONFIG: machineId => `i/v2/machines/${machineId}/graphics-config`,
  GET_DIGITAL_TWIN_SCENE: machineId => `i/v2/machines/${machineId}/digital-twin/scene`,
  // Feed tab
  GET_FEEDS: 'i/v1/audit/feeds',
  // Annotations
  // Analyst Tab
  SUBMIT_ANALYST_ASSESSMENT: machine_id => `i/v1/machines/${machine_id}`,
  // Datascience related
  GET_MACHINE_TRAIN_HEALTH_CONDITION: machine_id =>
    `i/v1/machine-train-health-condition/${machine_id}`,
  GET_MACHINE_TRAIN_HEALTH_TREND: machineId => `i/v1/ahs/${machineId}/detailed`,
  MACHINE_HEALTH_SCORE_RECOMPUTE: machineId => `i/v1/ahs/${machineId}/recompute`,
  MACHINE_HEALTH_SCORE_RECOMPUTE_TASK_STATUS: taskId => `i/v1/ahs/recompute/${taskId}`,
  GET_MACHINE_HEALTH_TREND: machineId => `i/v1/ahs/${machineId}/trend`,
  GET_MACHINE_VIBRATION_TREND: machineId =>
    `i/v1/vibration-baseline/machines/${machineId}/vibration-trend`,
  GET_COMPONENT_HEALTH_TREND: componentId => `i/v1/component-health-score/${componentId}/trend`,
  GET_COMPONENT_HEALTH_SCORE_DETAILS: componentId => `i/v1/component-health-score/${componentId}`,
  LOCATION_BASELINE: locationId => `i/v1/vibration-baseline/${locationId}`,
  ERASE_AHS_SCORES: machineId => `i/v1/ahs/${machineId}`,
  GET_MACHINE_TREND: machineId => `i/v1/baseline/machines/${machineId}/trend`,
  GET_COMPONENT_TREND: componentId => `i/v1/baseline/components/${componentId}/trend`,
  COMPONENT_BASELINE: componentId => `i/v1/baseline/${componentId}`,
  VALIDATE_COMPONENT_BASELINE: componentId => `i/v1/baseline/${componentId}/validate`,
  GET_MACHINE_BHS_TREND: 'i/v1/bhs/trend',
  CREATE_BHS_MODEL: 'i/v1/bhs/models',
  GET_BHS_MODELS: 'i/v1/bhs/models',
  UPDATE_BHS_MODEL: 'i/v1/bhs/models',
  RESET_BHS_MODEL: 'i/v1/bhs/models/reset',
  GET_BHS_SCORE_DETAILS: 'i/v1/bhs/score-details',
  GET_BHS_LATEST_SCORE: 'i/v1/bhs/latest-score',
  BHS_RECOMPUTE: 'i/v1/bhs/recompute',
  BHS_RECOMPUTE_TASK_STATUS: taskId => `i/v1/bhs/recompute/${taskId}`,
  // Charts
  ASSET_CHARTS_COMPARISON: 'i/v1/asset-charts-data-request',
  ASSET_CHARTS_COMPARISON_RESULTS: taskId => `i/v1/asset-charts-data/${taskId}`,
  CHARTS_CONFIG_LIST: 'i/v1/charts-config',
  CHARTS_CONFIG: configId => `i/v1/charts-config/${configId}`,
  CHARTS_CONFIG_METADATA: 'i/v1/charts-config/metadata',
  GET_CHARTS_DATA: 'i/v1/charts-data',
  REORDER_CHARTS: 'i/v1/charts-config/sort',
  POST_OUTLIER_READING: (tag_id, timestamp) => `i/v1/tag-outlier-readings/${tag_id}/${timestamp}`,
  DELETE_OUTLIER_READING: (tag_id, timestamp) => `i/v1/tag-outlier-readings/${tag_id}/${timestamp}`,
  POST_DERIVED_TAG: 'i/v1/derived-tags',
  // Sensors
  GET_SENSORS_LIST: 'i/v2/sensors',
  SENSORS: 'i/v2/sensors',
  UPDATE_SENSOR_INFO: 'i/v2/sensors',
  UPDATE_SENSOR: serialNumber => `i/v2/sensors/${serialNumber}`,
  GET_SENSOR_DETAILS: serialNumber => `i/v2/sensors/${serialNumber}`,
  GET_SENSORS_METADATA: 'i/v2/sensors/metadata',
  GET_DEVICE_INFO_LOGS: serial_number => `i/v2/sensors/${serial_number}/info-logs`,
  GET_DEVICE_LOGS_DATA: mote_id => `i/v1/motes/${mote_id}/logs/features`,
  GET_TX_DEVICE_LOGS_DATA: tx_id => `i/v1/tx/${tx_id}/logs/features`,
  DOWNLOAD_DEVICES_LOGS: mote_id => `i/v1/motes/${mote_id}/download-logs?output=csv`,
  GET_DEVICE_LAST_REPORTED_INFO: serial_number => `i/v2/sensors/${serial_number}/last-reported-info`,
  DEVICE_CONFIG: serial_number => `i/v2/sensors/${serial_number}/config`,
  DEVICE_CONFIG_HISTORY: serial_number => `i/v2/sensors/${serial_number}/config-history`,
  GET_EVENT_METADATA: 'i/v2/sensors/event-configs/metadata',
  GET_ALARM_THRESHOLDS: measurement_location_id => `i/v2/sensors/event-configs/alarm-thresholds/${measurement_location_id}`,
  GET_DASHBOARD_COUPLING_ELIGIBILITY: location_id => `i/v2/sensors/event-configs/temperature-dashboard-coupling/${location_id}`,
  // Erroneous Readings
  GET_SUMMARIZED_ERRONEOUS_READINGS: 'i/v1/summarized-vibration-erroneous-readings',
  GET_ERRONEOUS_READINGS_WAVEFORM: tag_id => `i/v1/tags/${tag_id}/waveform`,
  GET_ERRONEOUS_READINGS_SPECTRUM: tag_id => `i/v1/tags/${tag_id}/spectrum`,
  ERRONEOUS_READINGS: id => id ? `i/v1/vibration-erroneous-readings/${id}` : 'i/v1/vibration-erroneous-readings',
  // BHS
  GET_TAGS_SPECTRUM_DATA: tag_id => `i/v1/tags-data/${tag_id}/spectrum`,
  GET_ANALYST_SUMMARY: machine_id => `i/v1/machines/${machine_id}/analyst-summaries/latest`,
  POST_ANALYST_SUMMARY: machine_id => `i/v1/machines/${machine_id}/analyst-summaries`,
  PUT_ANALYST_SUMMARY: (machine_id, summary_id) => `i/v1/machines/${machine_id}/analyst-summaries/${summary_id}`,
  // Asset Reports
  GET_ASSET_REPORTS: 'i/v1/asset-reports',
  GET_REPORT_FILE: fileName => `i/v1/asset-reports/file/${fileName}`,
  GENERATE_ASSET_REPORT: 'i/v1/asset-reports',
  GET_REPORT_TASK_STATUS: taskId => `i/v1/asset-reports/tasks/${taskId}`,
  GET_PENDING_REPORT_TASK: 'i/v1/asset-reports/pending-task',
  UPLOAD_ASSET_REPORT: 'i/v1/asset-reports/upload',
  DOWNLOAD_ASSET_REPORT: report_id => `i/v1/asset-reports/${report_id}`,
  // Asset Templates
  GET_ASSET_TEMPLATES: 'i/v1/asset-templates',
  CREATE_ASSET_TEMPLATE: 'i/v1/asset-templates',
  GET_ASSET_TEMPLATE: template_id => `i/v1/asset-templates/${template_id}`,
  UPDATE_ASSET_TEMPLATE: template_id => `i/v1/asset-templates/${template_id}`,
  DELETE_ASSET_TEMPLATE: template_id => `i/v1/asset-templates/${template_id}`,
  CREATE_ASSETS_FROM_TEMPLATES: 'i/v1/asset-templates/assets',
  UNASSIGNED_SENSORS: 'i/v1/tx-sensors',
  ASSIGN_SENSOR: portId => `i/v1/tx-port/${portId}/assign-sensor`,
  UNASSIGN_SENSOR: portId => `i/v1/tx-port/${portId}/unassign-sensor`,
  ASSIGN_SENSORS_TO_TX: 'i/v2/tx/assign-sensor',
  // RBAC
  GET_RBAC_PERMISSIONS: 'i/v1/rbac-rules',
  // hierarchy
  GET_ACCOUNT_HIERARCHY: 'i/v1/asset-hierarchies',
  UPDATE_AREAS: 'i/v1/areas',
  UPDATE_MACHINES: 'i/v1/machines',
  // machine diagnostics
  RULES_METADATA: 'i/v1/diagnostic-rules/metadata',
  RULES_RESULT: resultId => resultId ? `i/v1/diagnostic-results/${resultId}` : 'i/v1/diagnostic-results',
  RULES: ruleId => ruleId ? `i/v1/diagnostic-rules/${ruleId}` : 'i/v1/diagnostic-rules',
  FAILURE_MODE: failureModeId => failureModeId ? `i/v1/failure-mode/${failureModeId}` : 'i/v1/failure-modes',
  PARAMETERS_TREND: resultId => `i/v1/diagnostic-results/${resultId}/parameters-trend`,
  RULE_TEST_RUN: taskId => taskId ? `i/v1/diagnostic-rules/test-run/${taskId}` : 'i/v1/diagnostic-rules/test-run',
  // AHS feedback
  AHS_FEEDBACK: feedbackId => `i/v1/ahs-feedback/${feedbackId}`,
  DELETE_AREA: area_id => `i/v1/areas/${area_id}`,
  UPDATE_AREA: area_id => `i/v1/areas/${area_id}`,
  CREATE_AREA: 'i/v1/areas',
  // Devices Health
  GET_DEVICES_HEALTH_DATA: 'i/v1/analytics/devices',
  // Tags List
  GET_TAGS_LIST: 'i/v1/tags',
  TAGS: 'i/v1/tags',
  // Automatic alarm settings
  MACHINES_ALARM_SETTINGS: 'i/v1/machines/alarm-settings',
  // Tag template
  TAG_TEMPLATE: tagTemplateId => tagTemplateId ? `i/v1/tag-template/${tagTemplateId}` : 'i/v1/tag-template',
  TAG_TEMPLATE_METADATA: 'i/v1/tag-template/metadata',
  // AdminDashboard
  ACCOUNT_ANALYTICS: id => `i/v1/analytics/accounts/${id}`,
  ACCOUNT_ANALYTICS_SUMMARY: 'i/v1/analytics/accounts/summary',
  EVENT_ANALYTICS: 'i/v1/analytics/events',
  GET_MACHINE_RESOURCES: 'i/v1/machine-trains',
  GET_PAST_WEEK_ANALYTICS: 'i/v1/analytics/events/weekly',
  MEASUREMENT_DEVICE_MONITOR: 'i/v1/analytics/devices',
  MEASUREMENT_DEVICE_MONITOR_SUMMARY: 'i/v1/analytics/devices',
  // FirmwareDeployments
  GET_FIRMWARE_DEPLOYMENTS: 'i/v1/firmware-deployments',
  CREATE_FIRMWARE_DEPLOYMENT: 'i/v1/firmware-deployments',
  VERIFY_DEVICE_DEPLOYMENTS: 'i/v1/verify-device-deployments',
  GET_FIRMWARE_DEPLOYMENT_DETAILS: deployment_id => `i/v1/firmware-deployments/${deployment_id}`,
  GET_FIRMWARE_DEPLOYMENT_DEVICES: deployment_id => `i/v1/firmware-deployments/${deployment_id}/devices`,
  UPDATE_FIRMWARE_DEPLOYMENT: deployment_id => `i/v1/firmware-deployments/${deployment_id}`,
  DELETE_FIRMWARE_DEPLOYMENT: deployment_id => `i/v1/firmware-deployments/${deployment_id}`,
  RETRY_FIRMWARE_DEPLOYMENT: deployment_id => `i/v1/firmware-deployments/${deployment_id}/retry`,
  // Firmwares
  GET_FIRMWARES: 'i/v1/firmwares',
  // Sensors OPS-APP
  GET_ALL_OPS_TX: (all = 1, pagination = 0) => `i/v1/ops/tx?all=${all}&pagination=${pagination}`, // Always fixed not need to changes or add dynamic params
  GET_ALL_OPS_MOTES: (all = 1, pagination = 0) => `i/v1/ops/motes?all=${all}&pagination=${pagination}`, // Always fixed not need to changes or add dynamic params
  // Accounts
  GET_ACCOUNTSLIST: 'i/v1/accounts',
  ADD_ACCOUNT: 'i/v1/accounts',
  CLONE_MACHINES_TO_ACCOUNT: 'i/v1/machine-clone/clone-to-account',
  CLONE_MACHINES_TO_ACCOUNT_STATUS: task_id => `i/v1/machine-clone/clone-to-account/${task_id}`,
  GET_MACHINE_DEMO_TEMPLATES: 'i/v1/machines/demo-templates',
  // API Audit
  GET_API_AUDIT_METADATA: 'i/v1/api-audit/metadata',
  GET_API_AUDIT_LOGS: 'i/v1/api-audit/logs',
  // Event Replay (staff): replay stored history as events
  GET_EVENT_REPLAY_MACHINES: 'i/v1/event-replay/machines',
  EVENT_REPLAY_RUNS: 'i/v1/event-replay/runs',
  EVENT_REPLAY_RUN: runId => `i/v1/event-replay/runs/${runId}`,
  GENERATE_EVENT_REPLAY_RUN: runId => `i/v1/event-replay/runs/${runId}/generate`,
  // RUL
  GET_RUL_TREND: component_id => `i/v1/components/${component_id}/rul-trend`,
  UPDATE_RUL_SETTINGS: component_id => `i/v1/components/${component_id}/rul-settings`,
  GET_RUL_SETTINGS: component_id => `i/v1/components/${component_id}/rul-settings`,
  // Teams
  GET_TEAMS_LIST: 'i/v1/teams',
  UPDATE_TEAM: team_id => `i/v1/teams/${team_id}`,
  ADD_USER_TO_TEAM: team_id => `i/v1/teams/${team_id}/users`,
  DELETE_USER_FROM_TEAM: team_id => `i/v1/teams/${team_id}/users`,
  ADD_AREA_TO_TEAM: team_id => `i/v1/teams/${team_id}/areas`,
  DELETE_AREA_FROM_TEAM: team_id => `i/v1/teams/${team_id}/areas`,
  CREATE_TEAM: 'i/v1/teams',
  DELETE_TEAM: team_id => `i/v1/teams/${team_id}`,
  // Feature Selection
  GET_AHS_SELECTED_FEATURES_INFO: machine_id => `i/v1/ahs/${machine_id}/features`,
  UPDATE_AHS_SELECTED_FEATURES_INFO: machine_id => `i/v1/ahs/${machine_id}/features`,

  GET_ACTIVITY_UNREAD_COUNT: '/i/v1/activities/unread-count',

  // Endpoints for device serial numbers
  GET_TX_SERIAL_NUMBERS: '/i/v2/tx/serial-numbers',
  GET_MOTE_SERIAL_NUMBERS: '/i/v2/motes/serial-numbers',

  // Annotations endpoints (internal API)
  ANNOTATIONS: 'i/v1/annotations',
  // Metadata endpoints removed
  // Vibration Analysis AI
  TRIGGER_VIBRATION_ANALYSIS: 'i/v1/vibration-analysis/analyze',
  GET_VIBRATION_ANALYSIS: analysisId => `i/v1/vibration-analysis/${analysisId}`,
  GET_EVENT_VIBRATION_ANALYSES: eventId => `i/v1/events/${eventId}/vibration-analyses`,

  // Vibration Analysis Feedback
  CREATE_VIBRATION_ANALYSIS_FEEDBACK: analysisId => `i/v1/vibration-analysis/${analysisId}/feedback`,
  GET_VIBRATION_ANALYSIS_FEEDBACKS: analysisId => `i/v1/vibration-analysis/${analysisId}/feedback`,
  GET_VIBRATION_ANALYSIS_FEEDBACK: (analysisId, feedbackId) => `i/v1/vibration-analysis/${analysisId}/feedback/${feedbackId}`,
  UPDATE_VIBRATION_ANALYSIS_FEEDBACK: (analysisId, feedbackId) => `i/v1/vibration-analysis/${analysisId}/feedback/${feedbackId}`,
  DELETE_VIBRATION_ANALYSIS_FEEDBACK: (analysisId, feedbackId) => `i/v1/vibration-analysis/${analysisId}/feedback/${feedbackId}`,

  // Vibration Analysis Regeneration
  REGENERATE_VIBRATION_ANALYSIS: analysisId => `i/v1/vibration-analysis/${analysisId}/regenerate`,
  GET_VIBRATION_ANALYSIS_BY_INT_ID: analysisId => `i/v1/vibration-analysis/by-id/${analysisId}`,

  // Vibration Analysis Metadata
  GET_VIBRATION_ANALYSIS_METADATA: 'i/v1/metadata/vibration-analysis',
  GET_VIBRATION_AGENT_METADATA: 'i/v1/metadata/vibration-agent',

  // FMEA
  GET_MACHINE_FMEA: machineId => `i/v1/machines/${machineId}/fmea`,
  GET_MACHINE_FMEA_COMPOSED: machineId => `i/v1/machines/${machineId}/fmea/composed`,
  RESYNC_MACHINE_FMEA: machineId => `i/v1/machines/${machineId}/fmea/resync`,
  ASSIGN_FMEA_TO_MACHINE: machineId => `i/v1/machines/${machineId}/fmea/assign`,
  UNASSIGN_FMEA_FROM_MACHINE: machineId => `i/v1/machines/${machineId}/fmea/unassign`,
  GET_FMEA_TEMPLATES: 'i/v1/fmea/templates',
  CREATE_FMEA_TEMPLATE: 'i/v1/fmea/templates',
  GET_FMEA_TEMPLATE: templateId => `i/v1/fmea/templates/${templateId}`,
  UPDATE_FMEA_TEMPLATE: templateId => `i/v1/fmea/templates/${templateId}`,
  DELETE_FMEA_TEMPLATE: templateId => `i/v1/fmea/templates/${templateId}`,
  PROMOTE_FMEA_TEMPLATE: templateId => `i/v1/fmea/templates/${templateId}/promote`,
  FMEA_CHAT: machineId => `i/v1/machines/${machineId}/fmea/chat`,
  GET_FMEA_CONVERSATION: conversationId => `i/v1/fmea/conversations/${conversationId}`,
  // Component-level (global) FMEA templates
  GET_FMEA_COMPONENT_TEMPLATES: 'i/v1/fmea/component-templates',
  CREATE_FMEA_COMPONENT_TEMPLATE: 'i/v1/fmea/component-templates',
  GET_FMEA_COMPONENT_TEMPLATE: templateId => `i/v1/fmea/component-templates/${templateId}`,
  UPDATE_FMEA_COMPONENT_TEMPLATE: templateId => `i/v1/fmea/component-templates/${templateId}`,
  DELETE_FMEA_COMPONENT_TEMPLATE: templateId => `i/v1/fmea/component-templates/${templateId}`,
  PARSE_FMEA_COMPONENT_CSV: 'i/v1/fmea/component-templates/parse-csv',
  GET_FMEA_COMPONENT_TYPES: 'i/v1/fmea/component-types'
};
export const getFullEndPointUrl = endPointUrl => `${getBaseUrl()}/${endPointUrl}`;
export const getImpUrl = device_id => `https://impcentral.electricimp.com/ide/devices/${device_id}`;
export const getDeviceGCPLogsUrl = device_id => `https://console.cloud.google.com/logs/query?query=resource.type%3D%22generic_node%22%0Aresource.labels.node_id%3D%22${device_id}%22%0A-jsonPayload.module%3D%22ImpAgent%22&project=petasense`;
export const markersToSkip = new Set(['2xLF']);
export const TYPES = {
  GET_RESOURCES_BASED_ON_TYPE: 'GET_RESOURCES_BASED_ON_TYPE',
  GET_RESOURCES_BASED_ON_TYPE_REQUEST: 'GET_RESOURCES_BASED_ON_TYPE_REQUEST',
  GET_RESOURCES_BASED_ON_TYPE_SUCCESS: 'GET_RESOURCES_BASED_ON_TYPE_SUCCESS',
  GET_RESOURCES_BASED_ON_TYPE_FAILURE: 'GET_RESOURCES_BASED_ON_TYPE_FAILURE'
};
// js Date().getDay() returned values.
const dayNamesByIndex = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  0: 'Sunday'
};

// Fallback short weekday labels (2-letter style for Latin locales)
const shortDayNamesByIndex = {
  0: 'Su',
  1: 'Mo',
  2: 'Tu',
  3: 'We',
  4: 'Th',
  5: 'Fr',
  6: 'Sa'
};
export const mapDayToString = new Proxy({}, {
  get: (_target, day) => {
    if (typeof day === 'symbol') return undefined;
    const dayIndex = Number(day);
    return i18n.t(`common.weekdays.${dayIndex}`, {
      defaultValue: dayNamesByIndex[dayIndex] || ''
    });
  }
});

export const mapShortDayToString = new Proxy({}, {
  get: (_target, day) => {
    if (typeof day === 'symbol') return undefined;
    const dayIndex = Number(day);
    return i18n.t(`common.shortWeekdays.${dayIndex}`, {
      defaultValue: shortDayNamesByIndex[dayIndex] || ''
    });
  }
});
export const OVERALL_ALARMS_LIST = [
  'pressure_critical',
  'pressure_warning',
  'temperature_critical',
  'temperature_warning',
  'vibration_critical',
  'vibration_warning'
];
export const SEVERITY_CSS_CLASS_MAP = {
  good: 'ps-good-health',
  slight: 'ps-slight-health',
  moderate: 'ps-moderate-health',
  warning: 'ps-warning-health',
  critical: 'ps-critical-health',
  null: 'ps-unknown-health',
  undefined: 'ps-unknown-health'
};
export const MACHINE_CONDITION = [
  { text: 'Good', value: 'good', color: theme.colors.normal, type: 'bullet' },
  { text: 'Slight', value: 'slight', color: theme.colors.slight, type: 'bullet' },
  { text: 'Moderate', value: 'moderate', color: theme.colors.moderate, type: 'bullet' },
  { text: 'Warning', value: 'warning', color: theme.colors.warning, type: 'bullet' },
  { text: 'Critical', value: 'critical', color: theme.colors.critical, type: 'bullet' }
];
export const getMachineConditionOptions = (assetConditionLevels, t = i18n.t.bind(i18n)) => {
  const options = MACHINE_CONDITION
    .map(opt => ({
      ...opt,
      text: t(`machineCondition.${opt.value}`, { defaultValue: opt.text })
    }))
    .slice();
  if (assetConditionLevels === 3) options.splice(1, 2);
  return options;
};
export const KEYBOARD_CODES = {
  ESC: 27,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
  UP_ARROW: 38,
  DOWN_ARROW: 40,
  ENTER: 13,
  H: 72,
  S: 83
};
export const EVENT_TYPES = {
  MOTE_UNREACHABLE: 'mote_unreachable',
  TX_UNREACHABLE: 'tx_unreachable'
};
export const CONDITION_MAP = {
  good: 'GOOD',
  slight: 'SLIGHT',
  moderate: 'MODERATE',
  warning: 'WARNING',
  critical: 'CRITICAL'
};
export const getMachineConditionColors = (assetConditionLevels) => {
  const options = getMachineConditionOptions(assetConditionLevels);
  const colorsArray = options.map(c => c.color).concat([theme.colors.unknown]);
  return colorsArray;
};
export const REVERSE_CONDITION_MAP = {
  Good: 'good',
  Slight: 'slight',
  Moderate: 'moderate',
  Warning: 'warning',
  Critical: 'critical',
  Unknown: null
};
export const getMachineConditionLabels = (assetConditionLevels, t = i18n.t.bind(i18n)) => {
  const options = getMachineConditionOptions(assetConditionLevels, t);
  const labelsArray = options.map(c => c.text).concat([
    t('machineCondition.unknown', { defaultValue: 'Unknown' })
  ]);
  return labelsArray;
};
export const METRIC_UNIT_SYSTEM = 1;
export const IMPERIAL_UNIT_SYSTEM = 2;
export const SCROLL_REF_THRESHOLD = 50;
export const getUnitSystemOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('profileSettings.unitSystem.imperial', { defaultValue: 'Imperial' }), value: IMPERIAL_UNIT_SYSTEM },
  { text: t('profileSettings.unitSystem.metric', { defaultValue: 'Metric' }), value: METRIC_UNIT_SYSTEM }
]);
export const unitSystemMap = {
  1: { name: 'Metric', units: 'mm/s' },
  2: { name: 'Imperial', units: 'in/s' }
};
export const temperatureUnitSystemMap = {
  1: { name: 'Metric', units: '°C' },
  2: { name: 'Imperial', units: '°F' }
};
export const CRITICALITY_MAP = {
  1: 'Very High',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Very Low'
};
export const REVERSE_CRITICALITY_MAP = {
  'Very High': 1,
  High: 2,
  Medium: 3,
  Low: 4,
  'Very Low': 5
};
export const MACHINE_STATE = {
  0: 'OFF',
  1: 'ON',
  2: 'NA'
};
export const REVERSE_MACHINE_STATE_MAP = {
  OFF: 0,
  ON: 1,
  NA: 2
};
export const chartColors = [
  '#91B846',
  '#000000',
  '#37B6B8',
  '#EF5ECD',
  '#FF5C5C',
  '#FF9764',
  '#FFE25F',
  '#845317',
  '#05FBA1',
  '#FB056A',
  '#A6FF36',
  '#4E89F4'
];

export const overlayColors = [
  '#4E79A7', // Blue
  '#F28E2B', // Orange
  '#E15759', // Red
  '#76B7B2', // Teal
  '#59A14F', // Green
  '#EDC948', // Yellow
  '#B07AA1', // Purple
  '#FF9DA7', // Pink
  '#9C755F', // Brown
  '#BAB0AC', // Gray
  '#499894', // Dark Teal
  '#F1CE63', // Gold
  '#86BCB6', // Light Teal
  '#D37295', // Rose
  '#8CD17D', // Light Green
  '#FABFD2', // Light Pink
  '#B6992D', // Olive
  '#79706E', // Dark Gray
  '#D7B5A6', // Beige
  '#A0CBE8' // Light Blue
];

export const bearingFrequenciesDescription = [
  'Ball Pass Frequency, Inner Race',
  'Ball Pass Frequency, Outer Race',
  'Fundamental Train Frequency',
  'Ball Spin Frequency'
];
export const ColorAccToCondition = (condition, type = 'circle') => {
  switch (condition) {
    case 'good':
      return theme.colors.normal;
    case 'slight':
      return theme.colors.slight;
    case 'moderate':
      return theme.colors.moderate;
    case 'warning':
      return theme.colors.warning;
    case 'critical':
      return theme.colors.critical;
    default:
      return type === 'text' ? theme.colors.black : theme.colors.grey;
  }
};
// Must be generated at call-site so labels update when language changes.
export const getDefectOptions = (t = i18n.t.bind(i18n)) => ({
  1: { id: 1, name: t('common.defects.names.unbalance', { defaultValue: 'Unbalance' }), type: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  2: { id: 2, name: t('common.defects.names.angularMisalignment', { defaultValue: 'Angular Misalignment' }), type: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  3: { id: 3, name: t('common.defects.names.parallelMisalignment', { defaultValue: 'Parallel Misalignment' }), type: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  4: { id: 4, name: t('common.defects.names.bentShaft', { defaultValue: 'Bent Shaft' }), type: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  5: { id: 5, name: t('common.defects.names.rotatingLooseness', { defaultValue: 'Rotating Looseness' }), type: t('common.defects.types.looseness', { defaultValue: 'Looseness' }) },
  6: { id: 6, name: t('common.defects.names.structuralLooseness', { defaultValue: 'Structural Looseness' }), type: t('common.defects.types.looseness', { defaultValue: 'Looseness' }) },
  7: { id: 7, name: t('common.defects.names.resonance', { defaultValue: 'Resonance' }), type: t('common.defects.types.other', { defaultValue: 'Other' }) },
  8: { id: 8, name: t('common.defects.names.flowTurbulence', { defaultValue: 'Flow Turbulence' }), type: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  9: { id: 9, name: t('common.defects.names.cavitation', { defaultValue: 'Cavitation' }), type: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  10: { id: 10, name: t('common.defects.names.bladeVaneDefect', { defaultValue: 'Blade/Vane Defect' }), type: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  11: { id: 11, name: t('common.defects.names.stage1Defect', { defaultValue: 'Stage1 Defect' }), type: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  12: { id: 12, name: t('common.defects.names.stage2Defect', { defaultValue: 'Stage2 Defect' }), type: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  13: { id: 13, name: t('common.defects.names.stage3Defect', { defaultValue: 'Stage3 Defect' }), type: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  14: { id: 14, name: t('common.defects.names.stage4Defect', { defaultValue: 'Stage4 Defect' }), type: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  15: { id: 15, name: t('common.defects.names.toothWear', { defaultValue: 'Tooth Wear' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  16: { id: 16, name: t('common.defects.names.toothLoad', { defaultValue: 'Tooth Load' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  17: { id: 17, name: t('common.defects.names.eccentricity', { defaultValue: 'Eccentricity' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  18: { id: 18, name: t('common.defects.names.misalignment', { defaultValue: 'Misalignment' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  19: { id: 19, name: t('common.defects.names.brokenTooth', { defaultValue: 'Broken Tooth' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  20: { id: 20, name: t('common.defects.names.huntingTooth', { defaultValue: 'Hunting Tooth' }), type: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  21: { id: 21, name: t('common.defects.names.wornLoose', { defaultValue: 'Worn/Loose' }), type: t('common.defects.types.belt', { defaultValue: 'Belt' }) },
  22: { id: 22, name: t('common.defects.names.sheaveMisalignment', { defaultValue: 'Sheave Misalignment' }), type: t('common.defects.types.belt', { defaultValue: 'Belt' }) },
  23: { id: 23, name: t('common.defects.names.resonance', { defaultValue: 'Resonance' }), type: t('common.defects.types.belt', { defaultValue: 'Belt' }) }
});

export const getDefectOptionsArray = (t = i18n.t.bind(i18n)) => ([
  { id: 1, name: t('common.defects.names.unbalance', { defaultValue: 'Unbalance' }), header: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  { id: 2, name: t('common.defects.names.angularMisalignment', { defaultValue: 'Angular Misalignment' }), header: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  { id: 3, name: t('common.defects.names.parallelMisalignment', { defaultValue: 'Parallel Misalignment' }), header: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  { id: 4, name: t('common.defects.names.bentShaft', { defaultValue: 'Bent Shaft' }), header: t('common.defects.types.shaft', { defaultValue: 'Shaft' }) },
  { id: 5, name: t('common.defects.names.rotatingLooseness', { defaultValue: 'Rotating Looseness' }), header: t('common.defects.types.looseness', { defaultValue: 'Looseness' }) },
  { id: 6, name: t('common.defects.names.structuralLooseness', { defaultValue: 'Structural Looseness' }), header: t('common.defects.types.looseness', { defaultValue: 'Looseness' }) },
  { id: 7, name: t('common.defects.names.resonance', { defaultValue: 'Resonance' }), header: t('common.defects.types.other', { defaultValue: 'Other' }) },
  { id: 8, name: t('common.defects.names.flowTurbulence', { defaultValue: 'Flow Turbulence' }), header: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  { id: 9, name: t('common.defects.names.cavitation', { defaultValue: 'Cavitation' }), header: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  { id: 10, name: t('common.defects.names.bladeVaneDefect', { defaultValue: 'Blade/Vane Defect' }), header: t('common.defects.types.flow', { defaultValue: 'Flow' }) },
  { id: 11, name: t('common.defects.names.stage1Defect', { defaultValue: 'Stage1 Defect' }), header: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  { id: 12, name: t('common.defects.names.stage2Defect', { defaultValue: 'Stage2 Defect' }), header: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  { id: 13, name: t('common.defects.names.stage3Defect', { defaultValue: 'Stage3 Defect' }), header: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  { id: 14, name: t('common.defects.names.stage4Defect', { defaultValue: 'Stage4 Defect' }), header: t('common.defects.types.bearings', { defaultValue: 'Bearings' }) },
  { id: 15, name: t('common.defects.names.toothWear', { defaultValue: 'Tooth Wear' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 16, name: t('common.defects.names.toothLoad', { defaultValue: 'Tooth Load' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 17, name: t('common.defects.names.eccentricity', { defaultValue: 'Eccentricity' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 18, name: t('common.defects.names.misalignment', { defaultValue: 'Misalignment' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 19, name: t('common.defects.names.brokenTooth', { defaultValue: 'Broken Tooth' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 20, name: t('common.defects.names.huntingTooth', { defaultValue: 'Hunting Tooth' }), header: t('common.defects.types.gears', { defaultValue: 'Gears' }) },
  { id: 21, name: t('common.defects.names.wornLoose', { defaultValue: 'Worn/Loose' }), header: t('common.defects.types.belt', { defaultValue: 'Belt' }) },
  { id: 22, name: t('common.defects.names.sheaveMisalignment', { defaultValue: 'Sheave Misalignment' }), header: t('common.defects.types.belt', { defaultValue: 'Belt' }) },
  { id: 23, name: t('common.defects.names.resonance', { defaultValue: 'Resonance' }), header: t('common.defects.types.belt', { defaultValue: 'Belt' }) }
]);

export const OVERLAY_POINTS_LIMIT = 20;
export const getOverlayPointsLimitMessage = (t = i18n.t.bind(i18n)) => t('common.overlays.trendPointsLimitMessage', {
  defaultValue: 'Hold Ctrl (Cmd on Mac) and click the trend chart to select measurements for overlay (up to {{limit}}). View selected traces on the spectrum or waveform chart. Add optional descriptions in the legend.',
  limit: OVERLAY_POINTS_LIMIT
});
export const getOverlayPointLimitExceededMessage = (t = i18n.t.bind(i18n)) => t('common.overlays.trendPointLimitExceededMessage', {
  defaultValue: 'Maximum of {{limit}} points can be selected in overlay mode',
  limit: OVERLAY_POINTS_LIMIT
});
